<template>
  <div class="min-h-screen bg-gray-50 dark:bg-gray-900">
    <header class="bg-emerald-600 text-white py-6 px-4">
      <div class="max-w-4xl mx-auto">
        <h1 class="text-2xl font-bold mt-2">Settings</h1>
        <p class="mt-1 text-emerald-100">Display preferences save on this device. Your daily goal and email settings save to your account, so they follow you everywhere.</p>
      </div>
    </header>

    <main class="max-w-4xl mx-auto px-4 py-8 space-y-4">
      <!-- Dark mode -->
      <div class="flex items-center justify-between bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
        <div>
          <p class="font-semibold text-gray-900 dark:text-gray-100">Dark mode</p>
          <p class="text-sm text-gray-500 dark:text-gray-400">Use a dark colour theme.</p>
        </div>
        <button
          @click="toggleDark"
          class="relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors"
          role="switch"
          :aria-checked="dark"
          aria-label="Dark mode"
          :class="dark ? 'bg-emerald-600' : 'bg-gray-300'"
        >
          <span class="inline-block h-5 w-5 transform rounded-full bg-white transition-transform" :class="dark ? 'translate-x-5' : 'translate-x-0.5'"></span>
        </button>
      </div>

      <!-- Show IPA -->
      <div class="flex items-center justify-between bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
        <div>
          <p class="font-semibold text-gray-900 dark:text-gray-100">Show IPA pronunciation</p>
          <p class="text-sm text-gray-500 dark:text-gray-400">Display the full phonetic (IPA) guide instead of the simplified one.</p>
        </div>
        <button
          @click="toggleIpa"
          class="relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors"
          role="switch"
          :aria-checked="showIpa"
          aria-label="Show IPA pronunciation"
          :class="showIpa ? 'bg-emerald-600' : 'bg-gray-300'"
        >
          <span class="inline-block h-5 w-5 transform rounded-full bg-white transition-transform" :class="showIpa ? 'translate-x-5' : 'translate-x-0.5'"></span>
        </button>
      </div>

      <!-- Strict tone checking -->
      <div class="flex items-center justify-between bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
        <div>
          <p class="font-semibold text-gray-900 dark:text-gray-100">Strict tone checking</p>
          <p class="text-sm text-gray-500 dark:text-gray-400">Require exact tone marks. Off = accept answers that are right apart from tones, with a reminder.</p>
        </div>
        <button
          @click="toggleStrictTones"
          class="relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors"
          role="switch"
          :aria-checked="strictTones"
          aria-label="Strict tone checking"
          :class="strictTones ? 'bg-emerald-600' : 'bg-gray-300'"
        >
          <span class="inline-block h-5 w-5 transform rounded-full bg-white transition-transform" :class="strictTones ? 'translate-x-5' : 'translate-x-0.5'"></span>
        </button>
      </div>

      <!-- Autoplay audio -->
      <div class="flex items-center justify-between bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
        <div>
          <p class="font-semibold text-gray-900 dark:text-gray-100">Autoplay audio</p>
          <p class="text-sm text-gray-500 dark:text-gray-400">On: auto-plays audio where it helps (listening, speaking, tone drills) without giving away quiz answers. Off: silence — tap any audio button to play.</p>
        </div>
        <button
          @click="autoplayEnabled = !autoplayEnabled"
          class="relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors"
          role="switch"
          :aria-checked="autoplayEnabled"
          aria-label="Autoplay audio"
          :class="autoplayEnabled ? 'bg-emerald-600' : 'bg-gray-300'"
        >
          <span class="inline-block h-5 w-5 transform rounded-full bg-white transition-transform" :class="autoplayEnabled ? 'translate-x-5' : 'translate-x-0.5'"></span>
        </button>
      </div>

      <!-- Daily goal -->
      <div class="flex items-center justify-between bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
        <div>
          <p class="font-semibold text-gray-900 dark:text-gray-100">Daily XP goal</p>
          <p class="text-sm text-gray-500 dark:text-gray-400">Your target XP to earn each day.</p>
        </div>
        <select
          v-model.number="dailyGoal" @change="saveGoal"
          class="rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 dark:text-gray-100 px-3 py-2 text-sm"
        >
          <option v-for="g in goals" :key="g" :value="g">{{ g }} XP</option>
        </select>
      </div>

      <!-- Dialect (account-level, saved server-side) -->
      <div class="flex items-center justify-between bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
        <div>
          <p class="font-semibold text-gray-900 dark:text-gray-100">Accent</p>
          <p class="text-sm text-gray-500 dark:text-gray-400">Southern (Saigon) is the course default. Northern (Hanoi) swaps the audio — and the handful of words that differ — where available. Saved to your account.</p>
        </div>
        <div class="inline-flex rounded-lg border border-gray-200 dark:border-gray-600 p-1 shrink-0 ml-4">
          <button
            v-for="d in dialectOptions"
            :key="d.id"
            @click="setDialect(d.id)"
            class="px-3 py-1.5 rounded-md text-sm font-semibold transition-colors"
            :class="dialect === d.id ? 'bg-emerald-600 text-white' : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100'"
          >
            {{ d.label }}
          </button>
        </div>
      </div>

      <!-- Email notifications (account-level, saved server-side) -->
      <div class="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 space-y-5">
        <div class="flex items-center justify-between">
          <div>
            <p class="font-semibold text-gray-900 dark:text-gray-100">Practice reminders</p>
            <p class="text-sm text-gray-500 dark:text-gray-400">An email when your streak is about to break, or if you've been away a week. Saved to your account.</p>
          </div>
          <button
            @click="toggleEmailPref('reminder_emails')"
            :disabled="emailPrefsLoading"
            class="relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors disabled:opacity-50"
            role="switch"
            :aria-checked="emailPrefs.reminder_emails"
            aria-label="Streak reminder emails"
            :class="emailPrefs.reminder_emails ? 'bg-emerald-600' : 'bg-gray-300'"
          >
            <span class="inline-block h-5 w-5 transform rounded-full bg-white transition-transform" :class="emailPrefs.reminder_emails ? 'translate-x-5' : 'translate-x-0.5'"></span>
          </button>
        </div>
        <div class="flex items-center justify-between border-t border-gray-100 dark:border-gray-700 pt-5">
          <div>
            <p class="font-semibold text-gray-900 dark:text-gray-100">Weekly progress email</p>
            <p class="text-sm text-gray-500 dark:text-gray-400">A Monday summary of your XP, streak, and reviews due. Saved to your account.</p>
          </div>
          <button
            @click="toggleEmailPref('digest_emails')"
            :disabled="emailPrefsLoading"
            class="relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors disabled:opacity-50"
            role="switch"
            :aria-checked="emailPrefs.digest_emails"
            aria-label="Weekly progress email"
            :class="emailPrefs.digest_emails ? 'bg-emerald-600' : 'bg-gray-300'"
          >
            <span class="inline-block h-5 w-5 transform rounded-full bg-white transition-transform" :class="emailPrefs.digest_emails ? 'translate-x-5' : 'translate-x-0.5'"></span>
          </button>
        </div>
        <div class="flex items-center justify-between border-t border-gray-100 dark:border-gray-700 pt-5">
          <div>
            <p class="font-semibold text-gray-900 dark:text-gray-100">Product announcements</p>
            <p class="text-sm text-gray-500 dark:text-gray-400">Occasional news about new units and features. A few times a year at most. Saved to your account.</p>
          </div>
          <button
            @click="toggleEmailPref('announcement_emails')"
            :disabled="emailPrefsLoading"
            class="relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors disabled:opacity-50"
            role="switch"
            :aria-checked="emailPrefs.announcement_emails"
            aria-label="Product announcement emails"
            :class="emailPrefs.announcement_emails ? 'bg-emerald-600' : 'bg-gray-300'"
          >
            <span class="inline-block h-5 w-5 transform rounded-full bg-white transition-transform" :class="emailPrefs.announcement_emails ? 'translate-x-5' : 'translate-x-0.5'"></span>
          </button>
        </div>
      </div>

      <!-- Reset progress -->
      <div class="flex items-center justify-between bg-white dark:bg-gray-800 rounded-xl border border-red-200 dark:border-red-900/50 p-5">
        <div>
          <p class="font-semibold text-gray-900 dark:text-gray-100">Reset progress</p>
          <p class="text-sm text-gray-500 dark:text-gray-400">Erase all units, XP, and review history. This cannot be undone.</p>
        </div>
        <button
          @click="resetProgress"
          :disabled="resetting"
          class="text-sm font-semibold text-red-600 dark:text-red-400 border border-red-300 dark:border-red-800 hover:bg-red-50 dark:hover:bg-red-900/30 px-4 py-2 rounded-lg transition-colors disabled:opacity-50"
        >
          {{ resetting ? 'Resetting…' : 'Reset' }}
        </button>
      </div>
    </main>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue'
import { apiGet, apiPost } from '../api/client'
import { autoplayEnabled } from '../utils/autoplayPref'

const THEME_KEY = 'speaksaigon_theme'
const IPA_KEY = 'speaksaigon_show_ipa'
const GOAL_KEY = 'speaksaigon_daily_goal'
const STRICT_TONES_KEY = 'speaksaigon_strict_tones'
const goals = [20, 50, 100, 150, 200]

const props = defineProps({
  dialect: { type: String, default: 'south' },
})

// Account-wide accent preference, saved server-side (content surfaces read
// it). Optimistic with rollback, same pattern as the email prefs below.
const dialectOptions = [
  { id: 'south', label: 'Saigon' },
  { id: 'north', label: 'Hanoi' },
]
const dialect = ref(props.dialect)

async function setDialect(id) {
  if (dialect.value === id) return
  const previous = dialect.value
  dialect.value = id
  try {
    await apiPost('/api/v1/dialect', { dialect: id })
  } catch (_e) {
    dialect.value = previous
  }
}

const dark = ref(false)
const showIpa = ref(false)
const strictTones = ref(true)
const dailyGoal = ref(50)
const resetting = ref(false)

// Account-level email prefs live on the server (the mailer jobs read them).
// Disabled until loaded so a toggle can't race the initial fetch.
const emailPrefs = reactive({ reminder_emails: true, digest_emails: true, announcement_emails: true })
const emailPrefsLoading = ref(true)

async function toggleEmailPref(key) {
  const next = !emailPrefs[key]
  emailPrefs[key] = next // optimistic
  try {
    const data = await apiPost('/api/v1/email_preferences', { [key]: next })
    if (data) Object.assign(emailPrefs, data)
  } catch (e) {
    emailPrefs[key] = !next // roll back on failure
  }
}

async function resetProgress() {
  if (!confirm('Reset all progress? This cannot be undone.')) return
  resetting.value = true
  try {
    await apiPost('/api/v1/progress/reset', {})
    window.location.href = '/'
  } catch (e) {
    resetting.value = false
    alert('Could not reset progress. Please try again.')
  }
}

function read(key) { try { return localStorage.getItem(key) } catch (e) { return null } }
function write(key, value) { try { localStorage.setItem(key, value) } catch (e) { /* ignore */ } }

function toggleDark() {
  dark.value = !dark.value
  document.documentElement.classList.toggle('dark', dark.value)
  write(THEME_KEY, dark.value ? 'dark' : 'light')
}

function toggleIpa() {
  showIpa.value = !showIpa.value
  write(IPA_KEY, showIpa.value)
}

function toggleStrictTones() {
  strictTones.value = !strictTones.value
  write(STRICT_TONES_KEY, strictTones.value)
}

// The goal is the one display-adjacent setting that belongs to the ACCOUNT,
// not the device: the retention emails reference it, and a goal that resets
// when you open your laptop is worse than no goal. Still mirrored to
// localStorage so StatsBar can paint the ring before the request lands.
function saveGoal() {
  write(GOAL_KEY, dailyGoal.value)
  apiPost('/api/v1/account_prefs', { daily_goal: dailyGoal.value }).catch(() => {})
}

onMounted(async () => {
  try {
    const data = await apiGet('/api/v1/email_preferences')
    if (data) Object.assign(emailPrefs, data)
  } catch (e) { /* leave defaults; toggles still work */ }
  emailPrefsLoading.value = false

  dark.value = document.documentElement.classList.contains('dark')
  showIpa.value = read(IPA_KEY) === 'true'
  // Strict is the default — only lenient when explicitly turned off.
  strictTones.value = read(STRICT_TONES_KEY) !== 'false'
  const g = parseInt(read(GOAL_KEY), 10)
  if (!Number.isNaN(g)) dailyGoal.value = g
  apiGet('/api/v1/account_prefs')
    .then(prefs => { if (prefs?.daily_goal) dailyGoal.value = prefs.daily_goal })
    .catch(() => {})
})
</script>
